import React, { useState, useEffect } from 'react';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { MdVerified } from 'react-icons/md';
import { HiOutlinePhone } from "react-icons/hi";
import { MdOutlineMail } from "react-icons/md";
import { Helmet } from 'react-helmet';
import { FaCamera, FaUserCircle } from "react-icons/fa";
import CustomerPage from '../homeDashboard/templates/CustomerPage';
import DriverPage from '../driverDashboard/template/DriverPage';
import { updateUserProfile, fetchUserAddress, saveUserAddress, changeProfilePhoto, changePassword } from "../Services/ApiCallsServices/UserProfileApi"
import {useAuth} from "../context/AuthContext";

const UserProfilePage = () => {
    const [activeTab, setActiveTab] = useState('profile');
    const [isEditing, setIsEditing] = useState(false);
    const [profilePhoto, setProfilePhoto] = useState(null);
    const [notification, setNotification] = useState({ type: '', message: '' });
    const [role, setRole] = useState(null);
    const { auth } = useAuth();

    useEffect(() => {
        const fetchAddress = async () => {
            try {
                const data = await fetchUserAddress();
                setInitialAddress(data.address);
            } catch (error) {
                console.error('Error fetching address:', error);
            }
        };
        fetchAddress();
    }, []);



    const initialProfileValues = {

        email: auth.user?.email || '',
        title: auth.user?.title || '',
        firstName: auth.user?.first_name || '',
        lastName: auth.user?.last_name || '',
        phoneNumber: auth.user?.phone_number || '',
        countryCode: auth.user?.country_code || '',
    };

    const initialAddressValues = {
        unitNumber: '',
        complexName: '',
        streetNumber: '',
        streetName: '',
        suburb: '',
        city: '',
        postalCode: '',
        country: '',
        state: '',
    };

    const profileValidationSchema = Yup.object().shape({
        title: Yup.string().required('Title is required'),
        firstName: Yup.string().required('First name is required'),
        lastName: Yup.string().required('Last name is required'),
        phoneNumber: Yup.string().required('Phone number is required'),
    });

    const addressValidationSchema = Yup.object().shape({
        streetNumber: Yup.string().required('Street Number is required'),
        streetName: Yup.string().required('Street Name is required'),
        suburb: Yup.string().required('Suburb is required'),
        city: Yup.string().required('City is required'),
        postalCode: Yup.string().required('Postal Code is required'),
        country: Yup.string().required('Country is required'),
        state: Yup.string().required('State / Province is required'),
    });

    const passwordValidationSchema = Yup.object().shape({
        currentPassword: Yup.string().required('Current password is required'),
        newPassword: Yup.string().required('New password is required').min(6, 'Password must be at least 6 characters long'),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
            .required('Confirm password is required'),
    });

    const handleUpdate = async (values) => {
        try {
            const data = await updateUserProfile(values);
            setNotification({ type: 'success', message: data.message || 'Profile updated successfully' });
            setIsEditing(false);
        } catch (error) {
            setNotification({ type: 'error', message: 'Error occurred while updating Profile, all fields must be filled' });
        }
    };

    const camelToSnake = (obj) => {
        const newObj = {};
        for (const key in obj) {
            const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
            newObj[snakeKey] = obj[key];
        }
        return newObj;
    };

    const handleAddressUpdate = async (values) => {
        try {
            const snakeCased = camelToSnake(values);
            const data = await saveUserAddress(snakeCased);
            setNotification({ type: 'success', message: data.message || 'Address updated successfully' });
        } catch (error) {
            setNotification({ type: 'error', message: 'Error occurred while updating Address' });
        }
    };


    const handleProfilePhotoChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            const response = await changeProfilePhoto(auth.user.email, file);
            if (response?.profile_photo) {
                setProfilePhoto(response.profile_photo);
            }
            setNotification({ type: 'success', message: 'Profile photo updated successfully' });
        } catch (err) {
            setNotification({ type: 'error', message: 'Error uploading profile photo' });
        }
    };

    const handleChangePassword = async (values) => {
        try {
            const data = await changePassword(auth.user.email, values.currentPassword, values.newPassword);
            setNotification({ type: 'success', message: data.message || 'Password changed successfully' });
        } catch (error) {
            setNotification({ type: 'error', message: 'Error changing password' });
        }
    };

    const [initialAddress, setInitialAddress] = useState(initialAddressValues);


    const renderProfileTab = () => (
        <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex flex-col items-center mb-6">
                <div className="relative">
                    {profilePhoto ? (
                        <img 
                            src={profilePhoto} 
                            alt="Profile" 
                            className="w-32 h-32 rounded-full object-cover border-4 border-yellow-100" 
                        />
                    ) : (
                        <FaUserCircle className="w-32 h-32 text-gray-300 border-4 border-yellow-100 rounded-full" />
                    )}
                    <label 
                        htmlFor="upload-input"
                        className="absolute bottom-0 right-0 bg-yellow-400 p-2 rounded-full cursor-pointer hover:bg-yellow-500 transition"
                    >
                        <FaCamera className="text-white" />
                        <input 
                            type="file" 
                            id="upload-input" 
                            accept="image/*" 
                            onChange={handleProfilePhotoChange} 
                            className="hidden" 
                        />
                    </label>
                </div>
            </div>

            <div className="space-y-4">
                <div className="flex items-center text-gray-700">
                    <HiOutlinePhone className="text-yellow-500 mr-3 text-xl" />
                    <span>{initialProfileValues.phoneNumber}</span>
                </div>
                <div className="flex items-center text-gray-700">
                    <MdOutlineMail className="text-yellow-500 mr-3 text-xl" />
                    <span className="mr-2">{initialProfileValues.email}</span>
                    <MdVerified className="text-blue-500" />
                </div>
                <button 
                    onClick={() => setIsEditing(true)}
                    className="mt-4 px-6 py-2 bg-yellow-400 text-gray-800 font-semibold rounded-lg hover:bg-yellow-500 transition"
                >
                    Edit Info
                </button>
            </div>
        </div>
    );

    const renderEditingProfileTab = () => (
        <div className="bg-white rounded-xl shadow-md p-6">
            <Formik
                initialValues={initialProfileValues}
                validationSchema={profileValidationSchema}
                onSubmit={handleUpdate}
            >
                {({ isSubmitting }) => (
                    <Form className="space-y-4">
                        {['title', 'firstName', 'lastName', 'phoneNumber', 'countryCode'].map(field => (
                            <div key={field} className="space-y-1">
                                <label className="block text-sm font-medium text-gray-700">
                                    {field.charAt(0).toUpperCase() + field.slice(1)}
                                </label>
                                <Field 
                                    type="text" 
                                    name={field} 
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 outline-none transition" 
                                />
                                <ErrorMessage 
                                    name={field} 
                                    component="div" 
                                    className="text-sm text-red-500" 
                                />
                            </div>
                        ))}
                        <div className="flex space-x-4 pt-2">
                            <button 
                                type="submit" 
                                disabled={isSubmitting}
                                className="px-6 py-2 bg-yellow-400 text-gray-800 font-semibold rounded-lg hover:bg-yellow-500 transition disabled:opacity-50"
                            >
                                Save Changes
                            </button>
                            <button 
                                type="button" 
                                onClick={() => setIsEditing(false)}
                                className="px-6 py-2 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );

    const renderAddressTab = () => (
        <div className="bg-white rounded-xl shadow-md p-6">
            <Formik
                initialValues={initialAddressValues}
                validationSchema={addressValidationSchema}
                onSubmit={handleAddressUpdate}
            >
                {({ isSubmitting }) => (
                    <Form className="space-y-4">
                        {['unitNumber', 'complexName', 'streetNumber', 'streetName', 'suburb', 'city', 'postalCode', 'country', 'state'].map(field => (
                            <div key={field} className="space-y-1">
                                <label className="block text-sm font-medium text-gray-700">
                                    {field.split(/(?=[A-Z])/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                </label>
                                <Field 
                                    type="text" 
                                    name={field} 
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 outline-none transition" 
                                />
                                <ErrorMessage 
                                    name={field} 
                                    component="div" 
                                    className="text-sm text-red-500" 
                                />
                            </div>
                        ))}
                        <button 
                            type="submit" 
                            disabled={isSubmitting}
                            className="mt-4 px-6 py-2 bg-yellow-400 text-gray-800 font-semibold rounded-lg hover:bg-yellow-500 transition disabled:opacity-50"
                        >
                            Save Address
                        </button>
                    </Form>
                )}
            </Formik>
        </div>
    );

    const renderSecurityTab = () => (
        <div className="bg-white rounded-xl shadow-md p-6">
            <Formik
                initialValues={{ currentPassword: '', newPassword: '', confirmPassword: '' }}
                validationSchema={passwordValidationSchema}
                onSubmit={handleChangePassword}
            >
                {({ isSubmitting }) => (
                    <Form className="space-y-4">
                        {['currentPassword', 'newPassword', 'confirmPassword'].map(field => (
                            <div key={field} className="space-y-1">
                                <label className="block text-sm font-medium text-gray-700">
                                    {field.split(/(?=[A-Z])/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                </label>
                                <Field 
                                    type="password" 
                                    name={field} 
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 outline-none transition" 
                                />
                                <ErrorMessage 
                                    name={field} 
                                    component="div" 
                                    className="text-sm text-red-500" 
                                />
                            </div>
                        ))}
                        <button 
                            type="submit" 
                            disabled={isSubmitting}
                            className="mt-4 px-6 py-2 bg-yellow-400 text-gray-800 font-semibold rounded-lg hover:bg-yellow-500 transition disabled:opacity-50"
                        >
                            Change Password
                        </button>
                    </Form>
                )}
            </Formik>
        </div>
    );

    const MasterComponent = role === 'driver' || role === 'admin' ? DriverPage : CustomerPage;

    return (
        <MasterComponent>
            <Helmet>
                <title>Profile | Storage Sprint</title>
            </Helmet>
            <div className="p-4 space-y-6">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                <span className="text-yellow-500">Personal</span> Info
            </h1>

                <div className="flex border-b border-gray-200">
                    {['profile', 'address', 'security'].map(tab => (
                        <button
                            key={tab}
                            className={`px-4 py-2 font-medium text-sm relative ${
                                activeTab === tab 
                                    ? 'text-yellow-600 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-yellow-400'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </div>

                {notification.message && (
                    <div className={`p-3 rounded-lg ${
                        notification.type === 'success' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                    }`}>
                        {notification.message}
                    </div>
                )}

                {activeTab === 'profile' && !isEditing && renderProfileTab()}
                {activeTab === 'profile' && isEditing && renderEditingProfileTab()}
                {activeTab === 'address' && renderAddressTab()}
                {activeTab === 'security' && renderSecurityTab()}
            </div>     
        </MasterComponent>
    );
};

export default UserProfilePage;